import { useEffect, useState } from "react";

import { useMedplum } from "@medplum/react";

import { Box, Button, Drawer, Group, Select, Stack } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";

import * as Sentry from "@sentry/react";

import {
  PLACE_OF_SERVICE_OPTIONS,
  PlaceOfServiceValue,
} from "@/billing/constants/place-of-service";
import { updateEncounterChargeItemsLocation } from "@/encounters/services/encounter-charge-items";
import { UpdateEncounterChargeItemsLocationFailureReason } from "@/encounters/types/encounter-charge-items";
import { updateLocationFormSchema } from "@/encounters/zod-schemas/update-location";
import { showSystemErrorNotification, toast } from "@/utils/notifications";

type UpdateLocationFormValues = {
  placeOfService: PlaceOfServiceValue | undefined;
};

type UpdateLocationDrawerProps = {
  opened: boolean;
  onClose: () => void;
  encounterId: string;
  /** The Location the encounter's charges already carry, or undefined when none do. */
  currentPlaceOfService: string | undefined;
  onSaved: (placeOfService: string) => void;
};

/** What to tell the biller for each way the update could fail. */
const LOCATION_UPDATE_FAILURE_MESSAGES: Record<
  UpdateEncounterChargeItemsLocationFailureReason,
  string
> = {
  missing_encounter_id: "This encounter could not be found.",
  missing_place_of_service: "Choose a location before saving.",
  existing_charges_read_failed:
    "This encounter's procedure charges could not be loaded. Try again or contact support if this continues.",
  write_failed: "The location could not be saved. Try again or contact support if this continues.",
};

/**
 * Drawer that lets a biller set the Location every `ChargeItem` on an encounter carries, and save
 * immediately.
 *
 * Never touches the `Encounter` resource: Location lives on the charges themselves.
 */
function UpdateLocationDrawer({
  opened,
  onClose,
  encounterId,
  currentPlaceOfService,
  onSaved,
}: UpdateLocationDrawerProps) {
  const medplum = useMedplum();
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm<UpdateLocationFormValues>({
    initialValues: { placeOfService: currentPlaceOfService as PlaceOfServiceValue | undefined },
    validate: zodResolver(updateLocationFormSchema),
  });

  useEffect(() => {
    if (!opened) return;

    const values = { placeOfService: currentPlaceOfService as PlaceOfServiceValue | undefined };
    form.setValues(values);
    form.resetDirty(values);
    form.clearErrors();
    // Re-seed when the drawer opens for the encounter's current Location.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, currentPlaceOfService]);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = async (values: UpdateLocationFormValues) => {
    if (!values.placeOfService) return;

    setIsSaving(true);
    try {
      const result = await updateEncounterChargeItemsLocation({
        medplum,
        encounterId,
        placeOfService: values.placeOfService,
      });

      if (!result.ok) {
        toast({
          type: "error",
          title: "Could not update location",
          message: LOCATION_UPDATE_FAILURE_MESSAGES[result.reason],
          autoClose: false,
        });
        return;
      }

      // The encounter's last captured charge can be removed while this drawer is open — an ok
      // result with nothing in it means there was no ChargeItem left to stamp the Location onto.
      if (!result.data.length) {
        toast({
          type: "error",
          title: "Could not update location",
          message: "This encounter no longer has procedure charges to update.",
          autoClose: false,
        });
        return;
      }

      toast({
        type: "success",
        title: "Location updated",
        message: "The encounter's billing location has been saved.",
        autoClose: 3000,
      });
      onSaved(values.placeOfService);
      handleClose();
    } catch (error) {
      Sentry.captureException(error);
      showSystemErrorNotification("Could not update location");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer.Root
      opened={opened}
      position="right"
      onClose={handleClose}
      closeOnEscape={!isSaving}
      closeOnClickOutside={!isSaving}
    >
      <Drawer.Overlay />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Drawer.Content>
          <Drawer.Header mb="lg" px="md" bg="gray.0">
            <Group w="100%" justify="space-between">
              <Group gap="xs">
                <Drawer.CloseButton size="xl" disabled={isSaving} />
                <Drawer.Title c="dark.9" variant="bold">
                  Update Location
                </Drawer.Title>
              </Group>
              <Button type="submit" loading={isSaving} data-testid="update-location-save-button">
                Save
              </Button>
            </Group>
          </Drawer.Header>
          <Drawer.Body>
            <Stack mt="sm" gap="md">
              <Box data-testid="update-location-input">
                <Select
                  required
                  label="Location"
                  clearable={false}
                  disabled={isSaving}
                  placeholder="Select location"
                  data={[...PLACE_OF_SERVICE_OPTIONS]}
                  data-testid="update-location-select"
                  {...form.getInputProps("placeOfService")}
                />
              </Box>
            </Stack>
          </Drawer.Body>
        </Drawer.Content>
      </form>
    </Drawer.Root>
  );
}

export default UpdateLocationDrawer;
